import { useMemo, type SyntheticEvent } from 'react';
import { Autocomplete, Box, Stack, Typography } from '@locus-taxy/encore-mui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getTestId, buildFormTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../../utils/testIdUtils';
import {
  buildTimezoneOptionsWithOffset,
  getNextTimezone,
  getTimezoneOptionLabel,
  isTimezoneOptionEqualToValue,
  normalizeLocaleChangeValue,
  type TimezoneOption,
} from './localeSettingsSectionUtils';

interface LocaleSettingsSectionProps {
  readonly timezone: string;
  readonly timezoneOptions: string[];
  readonly locale: string;
  readonly localeOptions: string[];
  readonly onTimezoneChange: (timezone: string) => Promise<void>;
  readonly onLocaleChange: (locale: string) => Promise<void>;
  readonly isUpdating: boolean;
  readonly canUpdate: boolean;
}

export function LocaleSettingsSection({
  timezone,
  timezoneOptions,
  locale,
  localeOptions,
  onTimezoneChange,
  onLocaleChange,
  isUpdating,
  canUpdate,
}: LocaleSettingsSectionProps) {
  const { t } = useTranslation();

  const handleTimezoneChange = async (
    _event: SyntheticEvent<Element, Event>,
    newValue: string | TimezoneOption | (string | TimezoneOption)[] | null
  ) => {
    const nextTimezone = getNextTimezone(timezone, newValue);
    if (!nextTimezone) {
      return;
    }
    await onTimezoneChange(nextTimezone);
  };

  const handleLocaleChange = async (
    _event: unknown,
    newValue: string | string[] | null
  ) => {
    const value = normalizeLocaleChangeValue(newValue);
    if (value && value !== locale) {
      await onLocaleChange(value);
    }
  };

  const timezoneOptionsWithOffset = useMemo(() => {
    return buildTimezoneOptionsWithOffset(timezoneOptions, timezone);
  }, [timezoneOptions, timezone]);

  const selectedValue = useMemo(() => {
    // `timezoneOptionsWithOffset` always includes the current `timezone` (see options construction above),
    // so this lookup is expected to succeed.
    return timezoneOptionsWithOffset.find(option => option.value === timezone)!;
  }, [timezoneOptionsWithOffset, timezone]);

  return (
    <Box
      sx={{
        borderRadius: 1,
        mb: 4,
        backgroundColor: 'white',
      }}
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'locale-settings-section',
        page: 'account',
      })}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('userDetails.localeSettings')}
        </Typography>
        <Stack spacing={2} maxWidth={600}>
          <Autocomplete
            options={timezoneOptionsWithOffset}
            value={selectedValue}
            clearable={false}
            label={t('userDetails.timezone')}
            getOptionLabel={getTimezoneOptionLabel}
            isOptionEqualToValue={isTimezoneOptionEqualToValue}
            onChange={handleTimezoneChange}
            disabled={isUpdating || !canUpdate}
            data-testid={buildFormTestId({
              fieldType: 'select',
              fieldName: 'timezone',
              entity: 'account',
              formAction: 'update',
            })}
          />
          <Autocomplete
            options={localeOptions.length > 0 ? localeOptions : [locale]}
            value={locale}
            clearable={false}
            label={t('userDetails.language')}
            getOptionLabel={option => option}
            isOptionEqualToValue={(opt, val) => opt === val}
            onChange={handleLocaleChange}
            disabled={isUpdating || !canUpdate}
            data-testid={buildFormTestId({
              fieldType: 'select',
              fieldName: 'locale',
              entity: 'account',
              formAction: 'update',
            })}
          />
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          {t('userDetails.localeSettingsDescription')}
        </Typography>
      </Box>
    </Box>
  );
}
